import { Button, Flex, Text } from '@lideralia/alife-uikit';
import { CardCollapsible, CardPayInfo } from '@lideralia/alife-uikit/dist/organisms/Cards';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CALL_TO_ACTION_TYPES } from '../../../common/constants/callToActions';
import { COUNTRIES_LIST } from '../../../utilities';

const ServiceProduct = ({
  product,
  index,
  stripeKey,
  stripeAccount,
  company,
  locale,
  handleSubmit,
  handleModalShow,
  isOpen,
  onToggle,
  buttonDisabled,
}) => {
  const [showPayment, setShowPayment] = useState(false);
  const containerRef = useRef(null);

  // Accept click event so we can scroll when closing the payment panel
  const handleButtonClick = (event) => {
    // capture the clicked button element before event is released
    const button = event.currentTarget;
    if (product.call_to_action === CALL_TO_ACTION_TYPES.HIRE && !product.is_included) {
      setShowPayment((prev) => {
        // if the panel was open (prev=true), defer scroll to after close
        if (!prev) {
          setTimeout(() => {
            // scroll with 60px offset from top
            const top = button.getBoundingClientRect().top + window.pageYOffset - 60;
            window.scrollTo({ behavior: 'smooth', top });
          }, 100);
        }
        return !prev;
      });
    } else {
      handleModalShow();
    }
  };

  const handleOnToggle = () => {
    // when opening (isOpen false -> true), scroll the card container to the top
    if (!isOpen) {
      setTimeout(() => {
        const el = containerRef.current;
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 60;
          window.scrollTo({ behavior: 'smooth', top });
        }
      }, 0);
    }
    setShowPayment(false);
    onToggle();
  };

  return (
    <div ref={containerRef}>
      <CardCollapsible
        key={product.uuid || index}
        marginBottom={2}
        id={`service-product-${product.uuid || index}`}
        borderColor={
          product.is_recommended ? company?.style?.primaryColor || 'orange' : 'transparent'
        }
        isOpen={isOpen}
        onToggle={handleOnToggle}
      >
        <CardCollapsible.Header minHeight="60px" display="flex" alignItems="center">
          {!!product.is_recommended && (
            <Flex
              position="absolute"
              top={0}
              right={0}
              paddingLeft="8px"
              paddingRight={2}
              paddingTop="2px"
              paddingBottom="3px"
              backgroundColor={company?.style?.primaryColor || 'orange'}
              color={company?.style?.textColor || 'white'}
              borderRadius="8px 0 0 8px"
            >
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  marginRight: '2px',
                }}
              >
                ★
              </div>
              <div style={{ fontSize: '10px' }}>
                <FormattedMessage id="serviceProducts.recommended" />
              </div>
            </Flex>
          )}

          <Flex flexDirection="column">
            <Text color="black96" sizeText="display14" fontWeight="bold">
              {product.name}
            </Text>
            {product.subtitle && (
              <Text color="black64" sizeText="display12">
                {product.subtitle}
              </Text>
            )}
          </Flex>
        </CardCollapsible.Header>
        <CardCollapsible.Content>
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: '100%',
                aspectRatio: '204 / 180',
                objectFit: 'cover',
                marginBottom: '16px',
                borderRadius: '18px',
                display: 'block',
              }}
            />
          )}
          <Text color="black84" sizeText="display14" marginBottom={2}>
            {product.description &&
              product.description.split('\n').map((line, index) => (
                <React.Fragment key={`desc-${product.uuid || 'product'}-${line}`}>
                  {line}
                  {index < product.description.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
          </Text>
          {product.total > 0 && (
            <Flex marginTop={2} width="100%" justifyContent="right" alignItems="flex-end">
              <Text color="black60" sizeText="display24" fontWeight="bold">
                {product.total}
              </Text>
              <Text color="black60" sizeText="display16" fontWeight="bold" marginLeft="2px">
                {product.currency}
              </Text>
            </Flex>
          )}

          {product.user_already_ordered && (
            <Flex marginTop={2} width="100%" justifyContent="center">
              <Flex
                flexDirection="row"
                alignItems="center"
                backgroundColor="#fdf5e6"
                border="1px solid orange"
                borderRadius="100px"
                width="fit-content"
                paddingX={2}
                paddingY="2px"
                marginBottom={2}
              >
                <Text color="black84" sizeText="display12">
                  <FormattedMessage id="serviceProducts.alreadyOrdered" />
                </Text>
              </Flex>
            </Flex>
          )}
          {!!(
            product.call_to_action &&
            product.call_to_action_text &&
            !product.user_already_ordered
          ) && (
            <Button
              backgroundColor={company?.style?.primaryColor || 'orange'}
              color={company?.style?.textColor || 'white'}
              variant="primary"
              width="100%"
              marginY={2}
              onClick={handleButtonClick}
            >
              {product.call_to_action_text}
            </Button>
          )}
          {product.additional_info && (
            <Text color="black84" sizeText="display12" textAlign="center">
              {product.additional_info.split('\n').map((line, index) => (
                <React.Fragment key={`additional-${product.uuid || 'product'}-${line}`}>
                  {line}
                  {index < product.additional_info.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </Text>
          )}
          {showPayment && (
            <CardPayInfo
              id="card-pay-info"
              paddingTop={2}
              maxWidth="100%"
              labelPayMethod={<FormattedMessage id="flowerEcommerce.labelPayMethod" />}
              labelCreditCard={<FormattedMessage id="flowerEcommerce.labelCreditCard" />}
              labelCreditCardNumber={
                <FormattedMessage id="flowerEcommerce.labelCreditCardNumber" />
              }
              labelCreditCardExpDate={
                <FormattedMessage id="flowerEcommerce.labelCreditCardExpDate" />
              }
              labelCreditCardCvc={<FormattedMessage id="flowerEcommerce.labelCreditCardCvc" />}
              mathodSelected="card"
              stripeKey={stripeKey}
              stripeAccount={stripeAccount}
              locale={locale}
              // BODY
              labelInvoice={<FormattedMessage id="flowerEcommerce.labelInvoice" />}
              labelNeedInvoice={<FormattedMessage id="flowerEcommerce.labelNeedInvoice" />}
              labelName={<FormattedMessage id="flowerEcommerce.labelName" />}
              name=""
              labelSurname={<FormattedMessage id="flowerEcommerce.labelSurname" />}
              surname=""
              labelDNI={<FormattedMessage id="flowerEcommerce.labelDNI" />}
              dni=""
              labelEmail={<FormattedMessage id="flowerEcommerce.labelEmail" />}
              email=""
              labelAddress={<FormattedMessage id="flowerEcommerce.labelAddress" />}
              address=""
              labelLocation={<FormattedMessage id="flowerEcommerce.labelLocation" />}
              location=""
              labelCP={<FormattedMessage id="flowerEcommerce.labelCP" />}
              cp=""
              labelProvince={<FormattedMessage id="flowerEcommerce.labelProvince" />}
              province=""
              labelCountry={<FormattedMessage id="flowerEcommerce.labelCountry" />}
              country="ES"
              // FOOTER
              labelButton={<FormattedMessage id="flowerEcommerce.labelButton" />}
              optionsCountry={COUNTRIES_LIST}
              customColor={company?.style?.primaryColor}
              customTextColor={company?.style?.textColor}
              buttonDisabled={buttonDisabled}
              handleSubmit={handleSubmit}
              labelTermsAndConditions={
                <FormattedMessage
                  id="flowerEcommerce.validatingMyOrderReadTheConditionsInterflora"
                  values={{
                    link: (...chunks) => (
                      <strong
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: company?.style?.primaryColor,
                        }}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          // setShowConditions(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            // setShowConditions(true);
                          }
                        }}
                      >
                        {chunks}
                      </strong>
                    ),
                  }}
                />
              }
            />
          )}
        </CardCollapsible.Content>
      </CardCollapsible>
    </div>
  );
};

export default ServiceProduct;
